import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import type { PartyKind } from "../../../domain/directory";

export interface PartyLinkProps {
	readonly kind: PartyKind;
	/** The party's id in the table that owns it, as its directory row names it. */
	readonly id: string;
	readonly className?: string;
	/** Called as the link is followed, before the router navigates: the rail's search box closes its dropdown on it. */
	readonly onClick?: () => void;
	/** What the link says: the party's name, usually. */
	readonly children: ReactNode;
}

type PartyPageLink = (props: Omit<PartyLinkProps, "kind">) => ReactNode;

/**
 * A link to the page of the record that owns a party, per kind: a person to People & HR, and every other kind to one
 * of the Directory's own pages. There is no fallback for a kind with nowhere to go — every kind the Directory knows has
 * a page of its own, and `Record<PartyKind, …>` is what refuses a fifth one until it has one too.
 */
const PARTY_LINKS: Readonly<Record<PartyKind, PartyPageLink>> = {
	person: ({ id, className, onClick, children }) => (
		<Link to="/people/$personId" params={{ personId: id }} className={className} onClick={onClick}>
			{children}
		</Link>
	),
	vendor: ({ id, className, onClick, children }) => (
		<Link to="/directory/vendors/$vendorId" params={{ vendorId: id }} className={className} onClick={onClick}>
			{children}
		</Link>
	),
	customer: ({ id, className, onClick, children }) => (
		<Link to="/directory/customers/$customerId" params={{ customerId: id }} className={className} onClick={onClick}>
			{children}
		</Link>
	),
	other: ({ id, className, onClick, children }) => (
		<Link to="/directory/other/$partyId" params={{ partyId: id }} className={className} onClick={onClick}>
			{children}
		</Link>
	),
};

/**
 * A party's name — or whatever `children` says — linking to the record that owns it, whichever kind it is: the
 * Directory's list, a document's linked parties and a work item's drawer all link a party this way.
 */
export function PartyLink({ kind, ...props }: PartyLinkProps) {
	return PARTY_LINKS[kind](props);
}
